import NavigationButtons from "../components/NavigationButtons";
import { useAuthState } from "../hooks/useAuth";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  StickyNote,
  RotateCcw,
  Layers,
  Shuffle,
  Users,
  Share2,
  UserRoundSearch,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import {
  getTutorialSubjectsPath,
  isTutorialLesson,
  TUTORIAL_ACTIONS,
} from "../constants/tutorialContent";
import {
  APPROVED_LESSON_TAGS,
  getFallbackTutorialTags,
} from "../constants/approvedTags";
import ApprovedTagChips from "../components/ApprovedTagChips";
import PublicLessonsCarousel from "../components/PublicLessonsCarousel";
import { articlesAPI, tagsAPI } from "../services/api";
import "./Tutorial.css";

const FALLBACK_TUTORIAL_TAGS = getFallbackTutorialTags();

const Tutorial = () => {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [approvedTags, setApprovedTags] = useState(APPROVED_LESSON_TAGS);
  const [tutorialLessons, setTutorialLessons] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadTutorialTags = async () => {
      const [tagsResult, lessonsResult] = await Promise.allSettled([
        tagsAPI.getApprovedTags("lesson"),
        articlesAPI.getPublicArticles(),
      ]);

      if (!isMounted) return;

      if (tagsResult.status === "fulfilled") {
        const nextTags = tagsResult.value.data?.tags || [];
        if (nextTags.length > 0) {
          setApprovedTags(nextTags);
        }
      }

      if (lessonsResult.status === "fulfilled") {
        setTutorialLessons(
          (Array.isArray(lessonsResult.value.data)
            ? lessonsResult.value.data
            : []
          ).filter((lesson) => isTutorialLesson(lesson)),
        );
      }
    };

    loadTutorialTags();

    return () => {
      isMounted = false;
    };
  }, []);

  const availableTags = useMemo(() => {
    if (tutorialLessons.length === 0) {
      return FALLBACK_TUTORIAL_TAGS;
    }

    const tagCounts = tutorialLessons.reduce((counts, lesson) => {
      const lessonTags = Array.isArray(lesson.tags) ? lesson.tags : [];

      lessonTags.forEach((tagSlug) => {
        counts.set(tagSlug, (counts.get(tagSlug) || 0) + 1);
      });

      return counts;
    }, new Map());

    return approvedTags
      .filter((tag) => tagCounts.has(tag.slug))
      .sort((left, right) => {
        const countDiff =
          (tagCounts.get(right.slug) || 0) - (tagCounts.get(left.slug) || 0);
        return countDiff || left.sortOrder - right.sortOrder;
      })
      .map((tag) => ({
        ...tag,
        count: tagCounts.get(tag.slug) || 0,
      }));
  }, [approvedTags, tutorialLessons]);
  const primaryTags = availableTags.slice(0, 7);
  const otherTags = availableTags.slice(primaryTags.length);
  const hasOtherTags = otherTags.length > 0;
  const navigateToTag = (tagSlug) =>
    navigate(
      `${getTutorialSubjectsPath(
        TUTORIAL_ACTIONS.READ,
      )}&view=all&tag=${encodeURIComponent(tagSlug)}`,
    );

  const steps = [
    {
      icon: BookOpen,
      title: "Pick a subject",
      description: "Browse public lessons or create your own",
      tone: "neutral",
      path: getTutorialSubjectsPath(TUTORIAL_ACTIONS.READ),
    },
    {
      icon: BookOpen,
      title: "Read",
      description: "Read the lesson content at your own pace",
      tone: "neutral",
      path: getTutorialSubjectsPath(TUTORIAL_ACTIONS.READ),
    },
    {
      icon: StickyNote,
      title: "Take notes",
      description: "Highlight text and create notes while reading",
      tone: "primary",
    },
    {
      icon: RotateCcw,
      title: "Repeat your notes",
      description: "Review notes with reading mode and speed control",
      tone: "primary",
      path: getTutorialSubjectsPath(TUTORIAL_ACTIONS.NOTES),
    },
    {
      icon: Layers,
      title: "Use flashcards",
      description: "Create and study flashcards from your lessons",
      tone: "warm",
      path: getTutorialSubjectsPath(TUTORIAL_ACTIONS.FLASHCARDS),
    },
    {
      icon: Shuffle,
      title: "Match them all",
      description: (
        <>
          Coming soon
          <br />
          Match flashcards
        </>
      ),
      disabled: true,
      tone: "warm",
    },
  ];

  const communitySteps = [
    {
      icon: Users,
      title: "Join communities",
      description: "Learn with people who share your topics",
      tone: "community",
    },
    {
      icon: Share2,
      title: "Share lessons",
      description: "Send your lessons directly to your community",
      tone: "community",
    },
    {
      icon: BookOpen,
      title: "Study together",
      description: "Read, take notes, and review in one shared place",
      tone: "community",
    },
  ];

  const peopleSteps = [
    {
      icon: UserRoundSearch,
      title: "Meet learners",
      description:
        "Find people who choose to make their learning profile visible",
      tone: "people",
    },
    {
      icon: Sparkles,
      title: "Follow interests",
      description: "Notice shared topics before you join a study space",
      tone: "people",
    },
    {
      icon: ShieldCheck,
      title: "Stay private",
      description:
        "Appear only when you turn your public profile visibility on",
      tone: "people",
    },
  ];

  return (
    <div className="tutorial-container">
      {user && <NavigationButtons guestRedirectPath="/signup" />}
      <h1 className="tutorial-title">
        Ask yourself: What would I like to learn today?
      </h1>
      <p className="tutorial-subtitle">
        Pick a subject · read · take notes · repeat your notes · use flashcards
        · match them all
      </p>

      <h2 className="tutorial-secondary-title">
        Or perhaps you seek a moment of reflection
      </h2>
      <p className="tutorial-meditation-text">
        Use notes as a practice for meditation or prayer.
        <br />
        Write your thoughts, repeat them mindfully, and find clarity through
        reflection.
      </p>

      <div className="tutorial-steps">
        {steps.map((step, index) => {
          const Icon = step.icon;

          return (
            <div
              key={index}
              className={`tutorial-step ${step.disabled ? "disabled" : ""} ${
                step.path ? "tutorial-step-clickable" : ""
              }`}
              onClick={() => {
                if (!step.disabled && step.path) {
                  navigate(step.path);
                }
              }}
            >
              <div
                className={`tutorial-step-icon tutorial-step-icon-${step.tone}`}
              >
                <Icon size={32} />
              </div>
              <h3 className="tutorial-step-title">{step.title}</h3>
              <p className="tutorial-step-description">{step.description}</p>
            </div>
          );
        })}
      </div>

      {availableTags.length > 0 && (
        <div className="tutorial-tags-section">
          <div className="tutorial-tags-primary-row">
            <ApprovedTagChips
              tags={hasOtherTags ? primaryTags : availableTags}
              onTagClick={navigateToTag}
            />
            {hasOtherTags && (
              <button
                type="button"
                className="approved-tag-chip tutorial-other-tags-button"
                onClick={() => setShowAllTags((current) => !current)}
                aria-expanded={showAllTags}
              >
                Other tags
              </button>
            )}
          </div>
          {hasOtherTags && showAllTags && (
            <div className="tutorial-tags-expanded">
              <ApprovedTagChips tags={otherTags} onTagClick={navigateToTag} />
            </div>
          )}
        </div>
      )}

      <div className="tutorial-community">
        <h2 className="tutorial-secondary-title">
          Learn together with Communities
        </h2>
        <p className="tutorial-subtitle">
          Share lessons, discover new topics, and grow with your group.
        </p>
        <div className="tutorial-community-steps">
          {communitySteps.map((step, index) => {
            const Icon = step.icon;

            return (
              <div key={index} className="tutorial-community-step">
                <div
                  className={`tutorial-step-icon tutorial-step-icon-${step.tone}`}
                >
                  <Icon size={28} />
                </div>
                <h3 className="tutorial-step-title">{step.title}</h3>
                <p className="tutorial-step-description">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="tutorial-people">
        <h2 className="tutorial-secondary-title">Meet People</h2>
        <p className="tutorial-subtitle">
          Discover visible profiles, follow shared interests, and keep your
          presence opt-in.
        </p>
        <div className="tutorial-community-steps">
          {peopleSteps.map((step, index) => {
            const Icon = step.icon;

            return (
              <div key={index} className="tutorial-community-step">
                <div
                  className={`tutorial-step-icon tutorial-step-icon-${step.tone}`}
                >
                  <Icon size={28} />
                </div>
                <h3 className="tutorial-step-title">{step.title}</h3>
                <p className="tutorial-step-description">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <p className="tutorial-methods-text">
        Your favorite learning methods in your personal learning space: books
        and notebooks, all in one.
      </p>
      <br />
      <p className="tutorial-methods-text tutorial-methods-text-secondary">
        Build communities around what you study, and meet people who share the
        same curiosity.
      </p>
      <PublicLessonsCarousel lessons={tutorialLessons} />
    </div>
  );
};

export default Tutorial;
